// src/pages/VibeFriendshipsResourcesPage.jsx - Friendships Edition Resources
import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  Phone,
  MessageCircle,
  BookOpen,
  Users,
  AlertTriangle,
  Heart,
  Brain,
  ExternalLink,
  Clock,
  MapPin,
  Stethoscope,
  UserCheck,
  Shield,
  Globe,
  Mail,
  Headphones,
  Video,
  Smartphone,
  Instagram,
  Badge,
  Youtube,
  Sparkles,
  Flame,
  Zap,
  Coffee,
  GamepadIcon,
  UserPlus,
  HandHeart,
  HeartHandshake,
} from 'lucide-react';
import Card from '../components/shared/Card';
import Button from '../components/shared/Button';
import { CRISIS_SUPPORT, CYBERBULLYING_REPORTING, knownCountry, writeCountryPref, emergencyCallAdvice, DIRECTORIES } from '../constants/crisisSupport';
import { IntimateImageAdvice } from '../components/help/HelpNow';

const VibeFriendshipsResourcesPage = () => {
  // The reader's own country: the one they chose (here, in a tool or in the
  // Coach), else their browser's region. It used to open on Australia for
  // everyone, so a teenager in Manchester saw Lifeline and 000 first. When we
  // can't tell, the page asks rather than guessing.
  const [activeCountry, setActiveCountry] = useState(() => knownCountry());
  const chooseCountry = (key) => {
    setActiveCountry(key);
    writeCountryPref(key);
  };
  const [activeSection, setActiveSection] = useState('resources'); // Changed default to resources

  // Crisis support comes from the shared, per-market verified table.
  // ⚠️ Never inline numbers here — see src/constants/crisisSupport.js.
  const crisisSupport = CRISIS_SUPPORT;
  const activeRegion = activeCountry ? crisisSupport[activeCountry] : null;
  const activeReporting = activeCountry ? CYBERBULLYING_REPORTING[activeCountry] : null;

  // Friendship-focused resources
  const friendshipResources = {
    books: [
      {
        title: 'The Like Switch',
        author: 'Jack Schafer & Marvin Karlins',
        description: 'Ex-FBI agent shares science-backed techniques for building instant connections and making friends.',
        category: 'Social Skills',
        reason: 'Practical techniques that actually work for making genuine connections',
        vibeLevel: '🎯',
      },
      {
        title: 'How to Be Yourself',
        author: 'Ellen Hendriksen',
        description: 'Quiet your inner critic and rise above social anxiety to find authentic friendships.',
        category: 'Social Anxiety',
        reason: 'Perfect for anyone who feels awkward in social situations',
        vibeLevel: '💪',
      },
      {
        title: 'The Art of Conversation',
        author: 'Catherine Blyth',
        description: 'Master the skills of great conversation and never run out of things to say.',
        category: 'Communication',
        reason: 'Turns small talk into an art form you can actually master',
        vibeLevel: '💬',
      },
      {
        title: 'Platonic',
        author: 'Dr. Marisa G. Franco',
        description: 'The science of making and keeping friends as an adult (works for teens too!).',
        category: 'Friendship Science',
        reason: 'Based on actual research about how friendships work and grow',
        vibeLevel: '🔬',
      },
      {
        title: 'The Friendship Cure',
        author: 'Kate Leaver',
        description: 'Why friendship is the key to health and happiness in the modern world.',
        category: 'Friendship & Wellbeing',
        reason: 'Shows why friendships matter more than ever in our digital age',
        vibeLevel: '❤️',
      },
      {
        title: 'Big Friendship',
        author: 'Aminatou Sow & Ann Friedman',
        description: 'How to keep friendships alive and thriving through life changes.',
        category: 'Maintaining Friendships',
        reason: 'Honest about the work it takes to maintain deep friendships',
        vibeLevel: '👯‍♀️',
      },
      {
        title: 'Sorry I\'m Late, I Didn\'t Want to Come',
        author: 'Jessica Pan',
        description: 'An introvert\'s year of saying yes to social challenges - funny and relatable.',
        category: 'Introvert\'s Guide',
        reason: 'Hilarious and honest about the struggle of putting yourself out there',
        vibeLevel: '😅',
      },
      {
        title: 'The Lonely Century',
        author: 'Noreena Hertz',
        description: 'Understanding why we\'re lonelier than ever and how to reconnect.',
        category: 'Modern Loneliness',
        reason: 'Explains why making friends feels harder now and what to do about it',
        vibeLevel: '🌍',
      },
    ],
    apps: [
      {
        name: 'Discord',
        type: 'Interest Communities',
        description: 'Servers built around games, art, music, coding, fandoms - almost anything. The friendships come from turning up to the same server regularly, not from adding lots of people.',
        platforms: ['iOS', 'Android', 'Web', 'Desktop'],
        cost: 'Free',
        vibeRating: '⭐⭐⭐⭐',
        ageNote: '13+. Stick to servers about a thing you actually like, keep your real name, school and location to yourself, and tell someone if an adult you have never met starts messaging you privately.',
      },
      {
        name: 'Your school clubs and teams',
        type: 'In person',
        description: 'Debating, drama, music, chess, robotics, the school paper, any sport. The single most reliable place to make a friend at your age, because you see the same people every week without having to arrange anything.',
        platforms: ['At school'],
        cost: 'Free',
        vibeRating: '⭐⭐⭐⭐⭐',
      },
      {
        name: 'Sport, dance or martial arts outside school',
        type: 'In person',
        description: 'A completely separate group of people means one bad week at school does not take your whole social life with it. Most clubs have a free trial session.',
        platforms: ['Local clubs'],
        cost: 'Varies - many councils subsidise it',
        vibeRating: '⭐⭐⭐⭐⭐',
      },
      {
        name: 'Youth groups',
        type: 'In person',
        description: 'Scouts, Guides, youth theatre, cadets, church or community youth groups. Built for exactly this, and used to people arriving on their own.',
        platforms: ['Local groups'],
        cost: 'Free or low cost',
        vibeRating: '⭐⭐⭐⭐',
      },
      {
        name: 'Volunteering',
        type: 'In person',
        description: 'Animal shelters, environment groups, community events, fundraising. Working on something together skips the awkward what-do-we-talk-about stage entirely.',
        platforms: ['Local orgs'],
        cost: 'Free',
        vibeRating: '⭐⭐⭐⭐',
      },
      {
        name: 'Your library or council',
        type: 'In person',
        description: 'Most councils run free youth programmes, workshops and holiday activities that almost nobody knows about. Worth ten minutes on your council website.',
        platforms: ['Local'],
        cost: 'Free',
        vibeRating: '⭐⭐⭐⭐',
      },
    ],
    socialMedia: [
      {
        platform: 'TikTok',
        account: '@hank.green',
        description: 'Science educator who talks about human connection and community',
        followers: '7M+',
        vibes: '🧬 Science + Connection',
      },
      {
        platform: 'Instagram',
        account: '@werenotreallystrangers',
        description: 'Connection games and prompts for deeper conversations',
        followers: '4M+',
        vibes: '💭 Deep Talks',
      },
      {
        platform: 'YouTube',
        account: 'Charisma on Command',
        description: 'Social skills and confidence building with practical examples',
        followers: '6M+',
        vibes: '💪 Confidence Building',
      },
      {
        platform: 'TikTok',
        account: '@connordewalsh',
        description: 'Friendship coach sharing tips for making and keeping friends',
        followers: '500K+',
        vibes: '🤝 Friendship Tips',
      },
      {
        platform: 'Instagram',
        account: '@lisaoliveratherapy',
        description: 'Therapist sharing wisdom about healthy relationships and boundaries',
        followers: '1M+',
        vibes: '🌟 Healthy Boundaries',
      },
      {
        platform: 'YouTube',
        account: 'The School of Life',
        description: 'Philosophy and psychology of relationships and human connection',
        followers: '8M+',
        vibes: '🎓 Deep Wisdom',
      },
    ],
    podcasts: [
      {
        name: 'Dear Sugars',
        host: 'Cheryl Strayed & Steve Almond',
        description: 'Advice on relationships, friendships, and navigating social challenges',
        vibes: '💌 Wise Advice',
      },
      {
        name: 'Friendship Onion',
        host: 'Dominic Monaghan & Billy Boyd',
        description: 'Two best friends discussing friendship, life, and everything in between',
        vibes: '😊 Wholesome Vibes',
      },
      {
        name: 'How to Be a Better Human',
        host: 'Chris Duffy',
        description: 'TED talks about connection, empathy, and building better relationships',
        vibes: '🚀 Self-Improvement',
      },
      {
        name: 'Conversations with People Who Hate Me',
        host: 'Dylan Marron',
        description: 'Building bridges and finding common ground with unlikely people',
        vibes: '🌉 Bridge Building',
      },
      {
        name: 'Terrible, Thanks for Asking',
        host: 'Nora McInerny',
        description: 'Real conversations about life\'s challenges including friendship loss and changes',
        vibes: '💯 No sugar-coating',
      },
    ],
  };

  // When to seek help for friendship issues
  const friendshipChallenges = {
    redFlags: [
      {
        title: 'Feeling Completely Isolated',
        description: 'When loneliness is overwhelming and affecting your mental health daily.',
        severity: 'moderate',
        emoji: '🏝️',
      },
      {
        title: 'Toxic Friendship Patterns',
        description: 'Being in friendships that consistently make you feel worse about yourself.',
        severity: 'moderate',
        emoji: '🚫',
      },
      {
        title: 'Social Anxiety Paralysis',
        description: 'When fear of social situations stops you from living your life.',
        severity: 'moderate',
        emoji: '😰',
      },
      {
        title: 'Bullying or Harassment',
        description: 'Being targeted, excluded, or harassed by peers online or in person.',
        severity: 'urgent',
        emoji: '⚠️',
      },
      {
        title: 'Friend Breakup Grief',
        description: 'Losing a close friend and struggling to cope with the loss.',
        severity: 'moderate',
        emoji: '💔',
      },
      {
        title: 'Peer Pressure Problems',
        description: 'Friends pushing you to do things that go against your values or safety.',
        severity: 'urgent',
        emoji: '🚨',
      },
    ],
    gettingHelp: [
      {
        title: 'Talk to a School Counsellor',
        description: 'They understand friendship dynamics and can help with social challenges.',
        steps: [
          'Request a private meeting about social concerns',
          'Be honest about what you\'re experiencing',
          'Ask about peer support groups at school',
          'Get help developing social skills if needed',
        ],
        icon: UserCheck,
      },
      {
        title: 'Join Support Groups',
        description: 'Connect with others facing similar friendship challenges.',
        steps: [
          'Look for social skills or friendship groups',
          'Try online communities for your interests',
          'Consider group therapy for social anxiety',
          'Join clubs or activities aligned with your interests',
        ],
        icon: Users,
      },
      {
        title: 'Practice with Professionals',
        description: 'Therapists can help you build confidence and social skills.',
        steps: [
          'Look for therapists specialising in social anxiety or relationships',
          'Consider group therapy for practicing social skills',
          'Try CBT for changing negative thought patterns about friendships',
          'Ask about role-playing and social skills training',
        ],
        icon: Brain,
      },
    ],
  };

  const ResourcesSection = () => (
    <div className="space-y-6">
      {/* Books */}
      <Card className="p-6 glassmorphism">
        <h3 className="text-xl font-bold text-white mb-6 flex items-center gap-2">
          <BookOpen className="w-6 h-6 text-vibe-lavender" />
          Books About Friendship That Actually Help 📚
        </h3>
        <div className="grid gap-4">
          {friendshipResources.books.map((book, index) => (
            <motion.div 
              key={index}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: index * 0.1 }}
              className="p-4 bg-white/5 rounded-xl border border-white/10 hover-lift transition-all"
            >
              <div className="flex justify-between items-start mb-3">
                <div className="flex-1">
                  <div className="flex items-center gap-2 mb-1">
                    <h4 className="text-white font-bold">{book.title}</h4>
                    <span className="text-lg">{book.vibeLevel}</span>
                  </div>
                  <p className="text-slate-400 text-sm">by {book.author}</p>
                  <span className="inline-block px-3 py-1 bg-vibe-lavender/20 text-vibe-lavender text-xs rounded-full mt-2 font-medium">
                    {book.category}
                  </span>
                </div>
              </div>
              <p className="text-slate-300 text-sm mb-3 leading-relaxed">{book.description}</p>
              <div className="text-sm">
                <span className="text-vibe-mint font-medium">Why it's great:</span>
                <span className="text-slate-300 ml-2">{book.reason}</span>
              </div>
            </motion.div>
          ))}
        </div>
      </Card>

      {/* Apps */}
      <Card className="p-6 glassmorphism">
        <h3 className="text-xl font-bold text-white mb-6 flex items-center gap-2">
          <Smartphone className="w-6 h-6 text-vibe-sky" />
          Where to Actually Meet People 📍
        </h3>
        <p className="text-slate-300 text-sm mb-6 leading-relaxed">
          You will notice there are no friend-matching apps on this list. Almost all of them -
          Bumble BFF, Meetup and the rest - are 18+ in their own terms, so they are not built
          for you and not safe for you. Neither are AI companion apps: talking to a bot is not
          practice for talking to people, and it tends to make the real thing feel harder rather
          than easier. Everything below is either age-appropriate or somewhere you can physically go.
        </p>
        <div className="grid md:grid-cols-2 gap-4">
          {friendshipResources.apps.map((app, index) => (
            <motion.div 
              key={index}
              initial={{ opacity: 0, scale: 0.95 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ delay: index * 0.1 }}
              className="p-4 bg-white/5 rounded-xl border border-white/10 hover-lift transition-all"
            >
              <div className="flex justify-between items-start mb-3">
                <div>
                  <h4 className="text-white font-bold text-lg">{app.name}</h4>
                  <span className="text-vibe-sky text-sm font-medium">{app.type}</span>
                </div>
                <div className="text-right">
                  <div className="text-lg">{app.vibeRating}</div>
                  <span className={`text-xs px-2 py-1 rounded ${
                    app.cost.includes('Free') 
                      ? 'bg-vibe-mint/20 text-vibe-mint'
                      : 'bg-vibe-gold/20 text-vibe-gold'
                  }`}>
                    {app.cost}
                  </span>
                </div>
              </div>
              <p className="text-slate-300 text-sm mb-3 leading-relaxed">{app.description}</p>
              <div className="flex flex-wrap gap-1">
                {app.platforms.map((platform, idx) => (
                  <span key={idx} className="px-2 py-1 bg-vibe-coral/20 text-vibe-coral text-xs rounded font-medium">
                    {platform}
                  </span>
                ))}
              </div>
              {app.ageNote && (
                <p className="mt-3 text-xs leading-relaxed text-vibe-gold/90 bg-vibe-gold/10 border border-vibe-gold/30 rounded-lg p-3">
                  <span className="font-semibold">Staying safe:</span> {app.ageNote}
                </p>
              )}
            </motion.div>
          ))}
        </div>
      </Card>

      {/* Social Media & Podcasts */}
      <div className="grid md:grid-cols-2 gap-6">
        <Card className="p-6 glassmorphism">
          <h3 className="text-lg font-bold text-white mb-4 flex items-center gap-2">
            <Users className="w-5 h-5 text-vibe-sunset" />
            Follow for Friendship Wisdom 📲
          </h3>
          <div className="space-y-4">
            {friendshipResources.socialMedia.map((account, index) => (
              <div key={index} className="p-3 bg-white/5 rounded-lg border border-white/10">
                <div className="flex items-center gap-2 mb-2">
                  {account.platform === 'TikTok' && <div className="w-4 h-4 bg-black rounded text-white text-xs flex items-center justify-center">T</div>}
                  {account.platform === 'Instagram' && <Instagram className="w-4 h-4 text-pink-400" />}
                  {account.platform === 'YouTube' && <Youtube className="w-4 h-4 text-red-400" />}
                  <span className="text-white font-medium">{account.account}</span>
                  <span className="text-xs text-slate-400">({account.followers})</span>
                </div>
                <p className="text-slate-300 text-sm mb-2">{account.description}</p>
                <span className="text-xs px-2 py-1 bg-vibe-sunset/20 text-vibe-sunset rounded-full">
                  {account.vibes}
                </span>
              </div>
            ))}
          </div>
        </Card>

        <Card className="p-6 glassmorphism">
          <h3 className="text-lg font-bold text-white mb-4 flex items-center gap-2">
            <Headphones className="w-5 h-5 text-vibe-mint" />
            Podcasts About Connection 🎧
          </h3>
          <div className="space-y-4">
            {friendshipResources.podcasts.map((podcast, index) => (
              <div key={index} className="p-3 bg-white/5 rounded-lg border border-white/10">
                <div className="flex items-center gap-2 mb-2">
                  <div className="w-2 h-2 bg-vibe-mint rounded-full"></div>
                  <span className="text-white font-medium">{podcast.name}</span>
                </div>
                <p className="text-slate-400 text-xs mb-2">with {podcast.host}</p>
                <p className="text-slate-300 text-sm mb-2">{podcast.description}</p>
                <span className="text-xs px-2 py-1 bg-vibe-mint/20 text-vibe-mint rounded-full">
                  {podcast.vibes}
                </span>
              </div>
            ))}
          </div>
        </Card>
      </div>
    </div>
  );

  const ProfessionalHelpSection = () => (
    <div className="space-y-6">
      {/* When to Get Help */}
      <Card className="p-6 glassmorphism">
        <h3 className="text-xl font-bold text-white mb-6 flex items-center gap-2">
          <AlertTriangle className="w-6 h-6 text-vibe-gold" />
          When Friendship Challenges Need Professional Support 🤔
        </h3>
        <div className="space-y-4">
          {friendshipChallenges.redFlags.map((flag, index) => (
            <motion.div 
              key={index}
              initial={{ opacity: 0, x: -20 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ delay: index * 0.1 }}
              className={`p-4 rounded-xl border hover-lift transition-all ${
                flag.severity === 'urgent' 
                  ? 'bg-gradient-to-r from-red-900/30 to-orange-900/30 border-red-500/40'
                  : 'bg-gradient-to-r from-yellow-900/30 to-orange-900/30 border-yellow-500/40'
              }`}
            >
              <div className="flex items-start gap-4">
                <div className="text-3xl">{flag.emoji}</div>
                <div className="flex-1">
                  <h4 className={`font-bold text-lg mb-2 ${
                    flag.severity === 'urgent' ? 'text-red-300' : 'text-yellow-300'
                  }`}>
                    {flag.title}
                  </h4>
                  <p className="text-slate-200 leading-relaxed">{flag.description}</p>
                  {flag.severity === 'urgent' && (
                    <div className="mt-3 flex items-center gap-2 text-red-200 font-medium">
                      <Zap className="w-4 h-4" />
                      <span>This needs immediate attention</span>
                    </div>
                  )}
                </div>
              </div>
            </motion.div>
          ))}
        </div>
      </Card>

      {/* How to Get Help */}
      <Card className="p-6 glassmorphism">
        <h3 className="text-xl font-bold text-white mb-6 flex items-center gap-2">
          <Heart className="w-6 h-6 text-vibe-mint" />
          Getting Help with Social Challenges 💚
        </h3>
        <div className="space-y-6">
          {friendshipChallenges.gettingHelp.map((guide, index) => (
            <motion.div 
              key={index}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: index * 0.15 }}
              className="p-5 bg-gradient-to-r from-white/5 to-white/10 rounded-xl border border-white/10"
            >
              <div className="flex items-start gap-4">
                <div className="w-12 h-12 bg-gradient-to-br from-vibe-mint to-vibe-sky rounded-xl flex items-center justify-center flex-shrink-0">
                  <guide.icon className="w-6 h-6 text-white" />
                </div>
                <div className="flex-1">
                  <h4 className="text-white font-bold text-lg mb-2">{guide.title}</h4>
                  <p className="text-slate-300 mb-4 leading-relaxed">{guide.description}</p>
                  <div className="space-y-2">
                    {guide.steps.map((step, stepIndex) => (
                      <div key={stepIndex} className="flex items-start gap-3">
                        <div className="w-6 h-6 bg-vibe-coral/20 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5">
                          <span className="text-vibe-coral text-sm font-bold">{stepIndex + 1}</span>
                        </div>
                        <span className="text-slate-300 text-sm leading-relaxed">{step}</span>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            </motion.div>
          ))}
        </div>
      </Card>
    </div>
  );

  const CrisisSection = () => (
    <div className="space-y-6">
      {/* Country Selector */}
      <Card className="p-6 bg-gradient-to-r from-vibe-coral/10 to-vibe-sunset/10 border-vibe-coral/30">
        <h3 className="text-lg font-semibold text-white mb-4 flex items-center gap-2">
          <Globe className="w-5 h-5 text-vibe-coral" />
          Where are you? Let's find local support 🌍
        </h3>
        <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
          {Object.entries(crisisSupport).map(([key, country]) => (
            <button
              key={key}
              onClick={() => chooseCountry(key)}
              className={`p-3 rounded-xl border transition-all text-left hover-lift ${
                activeCountry === key
                  ? 'border-vibe-coral bg-gradient-to-br from-vibe-coral/20 to-vibe-sunset/20 shadow-glow-coral'
                  : 'border-white/20 hover:border-vibe-coral/50 bg-white/5'
              }`}
            >
              <div className="flex items-center gap-2">
                <span className="text-2xl">{country.flag}</span>
                <span className="text-white font-medium">{country.name}</span>
              </div>
            </button>
          ))}
        </div>
      </Card>

      {!activeRegion && (
        <Card className="p-6 glassmorphism">
          <p className="text-slate-200">
            Choose where you are above to see free, confidential helplines for your country.
          </p>
          <p className="text-slate-300 text-sm mt-2">
            {emergencyCallAdvice()}{' '}
            <a
              href={`https://${DIRECTORIES[0].website}`}
              target="_blank"
              rel="noopener noreferrer"
              className="text-vibe-coral underline underline-offset-2"
            >
              {DIRECTORIES[0].name}
            </a>{' '}
            lists free helplines in more than 175 countries.
          </p>
        </Card>
      )}

      {/* Crisis Services */}
      {activeRegion && (
      <Card className="p-6 glassmorphism">
        <h3 className="text-xl font-bold text-white mb-6 flex items-center gap-2">
          <Phone className="w-6 h-6 text-vibe-coral" />
          Support Services - {activeRegion.name}
          <HeartHandshake className="w-5 h-5 text-vibe-gold" />
        </h3>
        <div className="grid gap-4">
          {activeRegion.services.map((service, index) => (
            <motion.div 
              key={index} 
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: index * 0.1 }}
              className={`p-4 rounded-xl border transition-all hover-lift ${
                service.youthFocused 
                  ? 'bg-gradient-to-r from-vibe-mint/10 to-vibe-sky/10 border-vibe-mint/30' 
                  : 'bg-white/5 border-white/10'
              }`}
            >
              <div className="flex items-start justify-between mb-3">
                <div className="flex-1">
                  <div className="flex items-center gap-2 mb-2">
                    <h4 className="text-white font-semibold text-lg">{service.name}</h4>
                    {service.youthFocused && (
                      <span className="px-2 py-1 bg-vibe-mint/20 text-vibe-mint text-xs rounded-full font-medium">
                        Teen Focused
                      </span>
                    )}
                    {service.popular && (
                      <span className="px-2 py-1 bg-vibe-gold/20 text-vibe-gold text-xs rounded-full font-medium flex items-center gap-1">
                        <Flame className="w-3 h-3" />
                        Popular
                      </span>
                    )}
                  </div>
                  <div className="flex items-center gap-3 mb-3">
                    <div className="flex items-center gap-2">
                      {service.type === 'call' && <Phone className="w-4 h-4 text-vibe-mint" />}
                      {service.type === 'text' && <MessageCircle className="w-4 h-4 text-vibe-sky" />}
                      {service.type === 'chat' && <MessageCircle className="w-4 h-4 text-vibe-lavender" />}
                      <span className="text-xl font-bold text-vibe-coral">{service.number}</span>
                    </div>
                    {service.hasChat && (
                      <span className="px-2 py-1 bg-vibe-sky/20 text-vibe-sky text-xs rounded">
                        + Chat Available
                      </span>
                    )}
                    {service.hasText && (
                      <span className="px-2 py-1 bg-vibe-lavender/20 text-vibe-lavender text-xs rounded">
                        + Text Available
                      </span>
                    )}
                  </div>
                  <p className="text-slate-300 text-sm leading-relaxed">{service.description}</p>
                </div>
                <a
                  href={`https://${service.website}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-vibe-coral hover:text-vibe-sunset transition-colors p-2 rounded-lg hover:bg-white/10"
                >
                  <ExternalLink className="w-5 h-5" />
                </a>
              </div>
              <div className="text-xs text-slate-400 flex items-center gap-2">
                <Globe className="w-3 h-3" />
                {service.website}
              </div>
            </motion.div>
          ))}
        </div>

        {activeRegion?.emergency && (
          <div className="mt-6 p-4 rounded-xl bg-vibe-coral/10 border border-vibe-coral/30">
            <div className="flex items-center gap-2 mb-1">
              <Phone className="w-4 h-4 text-vibe-coral" />
              <span className="text-white font-semibold">{activeRegion.emergency.name}</span>
            </div>
            <p className="text-xl font-bold text-vibe-coral">{activeRegion.emergency.number}</p>
            <p className="text-slate-300 text-sm">{activeRegion.emergency.description}</p>
          </div>
        )}
      </Card>
      )}

      {/* Getting online bullying taken down */}
      {activeReporting && (
        <Card className="p-6 glassmorphism">
          <h3 className="text-xl font-bold text-white mb-2 flex items-center gap-2">
            <Shield className="w-6 h-6 text-vibe-sky" />
            Reporting online bullying - {activeReporting.name}
          </h3>
          <p className="text-slate-300 text-sm mb-5">
            Report it on the app or site first. That step is what lets anyone else step in
            afterwards, so don't skip it. Before you do, keep a record: screenshot the messages,
            usernames and links. The one thing never to screenshot or save is a nude or sexual
            picture of anyone under 18 - write down where it is instead (see below).
          </p>
          <div className="p-4 rounded-xl bg-white/5 border border-white/10">
            <div className="flex flex-wrap items-center gap-2 mb-2">
              <h4 className="text-white font-semibold text-lg">{activeReporting.body}</h4>
              {activeReporting.canCompelRemoval && (
                <span className="px-2 py-1 bg-vibe-mint/20 text-vibe-mint text-xs rounded-full font-medium">
                  Can require removal
                </span>
              )}
            </div>
            {activeReporting.number && (
              <p className="text-xl font-bold text-vibe-coral mb-2">{activeReporting.number}</p>
            )}
            <p className="text-slate-300 text-sm leading-relaxed mb-3">{activeReporting.detail}</p>
            <div className="text-xs text-slate-400 flex items-center gap-2">
              <Globe className="w-3 h-3" />
              {activeReporting.website}
            </div>
          </div>
        </Card>
      )}

      <IntimateImageAdvice
        headingLevel={3}
        country={activeCountry}
        onCountryChange={(key) => (key && key !== 'other' ? chooseCountry(key) : setActiveCountry(null))}
      />
    </div>
  );

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900 pt-32 vibe-scrollbar">
      <div className="container mx-auto px-4 py-8">
        <div className="max-w-6xl mx-auto">
          {/* Header */}
          <motion.div
            initial={{ opacity: 0, y: -20 }}
            animate={{ opacity: 1, y: 0 }}
            className="text-center mb-12"
          >
            <motion.div
              initial={{ scale: 0.8 }}
              animate={{ scale: 1 }}
              transition={{ duration: 0.5 }}
              className="mx-auto mb-8 max-w-md"
            >
              <img
                src="/images/friendship-resources.png"
                alt="Friendship Resources"
                className="w-full h-auto rounded-2xl shadow-2xl shadow-purple-500/20"
              />
            </motion.div>

            <div className="relative">
              <h1 className="text-4xl md:text-5xl font-bold text-gradient-energy mb-4">
                Friendship Resources & Support
              </h1>
              <motion.div
                animate={{ rotate: [0, 10, -10, 0] }}
                transition={{ duration: 2, repeat: Infinity, repeatDelay: 3 }}
                className="absolute -top-2 -right-4 text-3xl"
              >
                🤝
              </motion.div>
            </div>
            <p className="text-lg text-slate-200 max-w-3xl mx-auto leading-relaxed">
              Books, apps, and support to help you build amazing friendships and navigate social challenges.
              Plus real help when you need it. 💫
            </p>
          </motion.div>

          {/* Navigation - Resources first */}
          <motion.div 
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="flex justify-center mb-8"
          >
            <div className="glassmorphism rounded-2xl p-2 flex gap-1 flex-wrap">
              {[
                { id: 'resources', label: 'Cool Resources', icon: Sparkles, color: 'text-purple-300' },
                { id: 'professional', label: 'Getting Support', icon: Heart, color: 'text-green-300' },
                { id: 'crisis', label: 'Need Help Now', icon: Phone, color: 'text-red-300' },
              ].map((tab) => (
                <button
                  key={tab.id}
                  onClick={() => setActiveSection(tab.id)}
                  className={`px-6 py-3 rounded-xl font-bold transition-all flex items-center gap-2 ${
                    activeSection === tab.id
                      ? 'bg-gradient-to-r from-vibe-coral to-vibe-sunset text-white shadow-vibe-md'
                      : 'text-slate-300 hover:text-white hover:bg-white/10'
                  }`}
                >
                  <tab.icon className={`w-5 h-5 ${activeSection === tab.id ? 'text-white' : tab.color}`} />
                  <span>{tab.label}</span>
                </button>
              ))}
            </div>
          </motion.div>

          {/* Content */}
          <AnimatePresence mode="wait">
            {activeSection === 'resources' && (
              <motion.div
                key="resources"
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -20 }}
                transition={{ duration: 0.3 }}
              >
                <ResourcesSection />
              </motion.div>
            )}

            {activeSection === 'professional' && (
              <motion.div
                key="professional"
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -20 }}
                transition={{ duration: 0.3 }}
              >
                <ProfessionalHelpSection />
              </motion.div>
            )}

            {activeSection === 'crisis' && (
              <motion.div
                key="crisis"
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -20 }}
                transition={{ duration: 0.3 }}
              >
                <CrisisSection />
              </motion.div>
            )}
          </AnimatePresence>

          {/* Footer */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.5 }}
            className="mt-12"
          >
            <Card className="p-6 glassmorphism border-vibe-sky/30">
              <div className="flex items-start gap-4">
                <div className="w-12 h-12 bg-gradient-to-br from-vibe-sky to-vibe-lavender rounded-xl flex items-center justify-center flex-shrink-0">
                  <HandHeart className="w-6 h-6 text-white" />
                </div>
                <div>
                  <h3 className="text-white font-bold text-lg mb-3 flex items-center gap-2">
                    Remember This 💝
                    <UserPlus className="w-5 h-5 text-vibe-gold" />
                  </h3>
                  <p className="text-slate-200 leading-relaxed">
                    Building friendships can be challenging, but you're not alone in this journey. 
                    These resources are here to help you develop social skills, find your people, and navigate the ups and downs of friendships. 
                    If you're struggling with loneliness, social anxiety, or friendship problems that are seriously affecting your wellbeing, 
                    don't hesitate to reach out for professional support. Everyone deserves meaningful connections. 🌟
                  </p>
                  <div className="mt-4 flex items-center gap-2 text-sm text-vibe-mint">
                    <Zap className="w-4 h-4" />
                    <span className="font-medium">You're worthy of great friendships exactly as you are. 💫</span>
                  </div>
                </div>
              </div>
            </Card>
          </motion.div>
        </div>
      </div>
    </div>
  );
};

export default VibeFriendshipsResourcesPage;